import { useTranslations } from "@services/strings";
import { ChevronLeft, House } from "lucide-react";

/**
 * The app's Unlock request screen as a mock-up in markup, in place of the
 * screenshots the previous site used: property, amount and tenor sliders, and
 * the resulting installment. Numbers are illustrative.
 */
export const AppUnlockScreen = ({
  amount = 4_000_000,
  years = 10,
  monthly = 90_900,
}: {
  amount?: number;
  years?: number;
  monthly?: number;
}) => {
  const { t, formatNumber } = useTranslations();

  const Row = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5 text-[11px]">
      <span className="text-slate-500">{label}</span>
      <span className="tabular font-semibold">{value}</span>
    </div>
  );

  const Bar = ({ fill }: { fill: number }) => (
    <div className="relative mt-2 h-1 rounded-full bg-slate-200" dir="ltr">
      <div
        className="h-full rounded-full bg-ink"
        style={{ width: `${fill}%` }}
      />
      <span
        className="absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full border-2 border-ink bg-white"
        style={{ left: `calc(${fill}% - 8px)` }}
      />
    </div>
  );

  return (
    <div className="flex h-full flex-col bg-white px-4 pb-4 pt-12 text-ink">
      <div className="flex items-center gap-2 text-[12px] font-semibold">
        <ChevronLeft size={14} className="rtl:rotate-180" />
        <span className="flex-1 text-center">{t("site.app.unlock")}</span>
      </div>

      <div className="mt-4 flex items-center gap-2.5 rounded-xl border border-ink px-3 py-2.5">
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-mist">
          <House size={14} />
        </span>
        <div className="min-w-0">
          <p className="truncate text-[12px] font-semibold">
            {t("site.app.propertyName")}
          </p>
          <p className="truncate text-[10px] text-slate-500">
            {t("site.app.developer")}
          </p>
        </div>
      </div>

      <p className="mt-4 text-[11px] font-medium">{t("site.app.amount")}</p>
      <div className="mt-1.5 rounded-xl bg-mist px-3 py-2.5">
        <span className="me-1.5 text-[10px] font-medium text-slate-500">
          EGP
        </span>
        <span className="tabular text-[18px] font-bold">
          {formatNumber(amount)}
        </span>
      </div>
      <Bar fill={62} />

      <p className="mt-4 text-[11px] font-medium">
        {t("site.estimator.tenor")}
      </p>
      <div className="mt-1.5 flex items-baseline justify-between rounded-xl bg-mist px-3 py-2.5">
        <span className="tabular text-[18px] font-bold">{years * 12}</span>
        <span className="text-[10px] text-slate-500">
          {t("site.app.months")}
        </span>
      </div>
      <Bar fill={100} />

      <div className="mt-4 flex flex-col gap-2">
        <Row
          label={t("site.estimator.monthly")}
          value={`EGP ${formatNumber(monthly)}`}
        />
      </div>

      <div className="mt-auto rounded-xl bg-ink py-3 text-center text-[12px] font-semibold text-white">
        {t("site.app.sendRequest")}
      </div>
    </div>
  );
};
